import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  Building2, 
  Users, 
  Truck, 
  Package, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3,
  Activity,
  Shield,
  Globe,
  RefreshCw,
  Loader2,
  Database,
  Target
} from 'lucide-react';

const SuperadminDashboard = () => {
  const { user, hasPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);


  useEffect(() => {
    if (hasPermission('view_dashboard')) {
      loadDashboardData();
    }
  }, [hasPermission]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 SuperadminDashboard: Carregando dados do sistema...');

      // Carregar todas as estatísticas em paralelo
             const [
        suppliersResult,
        productsResult,
        documentsResult,
        receivingsResult,
        usersResult,
        companiesResult,
        leadsResult
      ] = await Promise.allSettled([
        httpsCallable(functions, 'getSupplierStats')({}),
        httpsCallable(functions, 'getProductStats')({}),
        httpsCallable(functions, 'getDocumentStats')({}),
        httpsCallable(functions, 'getReceivingStats')({}),
        httpsCallable(functions, 'listUsers')({ page: 1, limit: 1000 }),
        httpsCallable(functions, 'listCompanies')({ page: 1, limit: 1000 }),
        httpsCallable(functions, 'getLeadsStats')({})
      ]);

      // Processar resultados
      const systemStats = {
        // Empresas
        total_empresas: companiesResult.status === 'fulfilled' ? 
          companiesResult.value.data?.data?.pagination?.total || 0 : 0,
        empresas_ativas: companiesResult.status === 'fulfilled' ? 
          companiesResult.value.data?.data?.items?.filter(c => c.ativo === true).length || 0 : 0,

        // Usuários
        total_usuarios: usersResult.status === 'fulfilled' ? 
          usersResult.value.data?.data?.pagination?.total || 0 : 0,
        usuarios_ativos: usersResult.status === 'fulfilled' ? 
          usersResult.value.data?.data?.items?.filter(u => u.status === 'ativo').length || 0 : 0,

        // Fornecedores
        total_fornecedores: suppliersResult.status === 'fulfilled' ? 
          suppliersResult.value.data?.data?.total || 0 : 0,
        fornecedores_aprovados: suppliersResult.status === 'fulfilled' ? 
          suppliersResult.value.data?.data?.por_status?.aprovado || 0 : 0,

        // Produtos
        total_produtos: productsResult.status === 'fulfilled' ? 
          productsResult.value.data?.data?.total || 0 : 0,
        produtos_ativos: productsResult.status === 'fulfilled' ? 
          productsResult.value.data?.data?.por_status?.ativo || 0 : 0,

        // Documentos
        documentos_pendentes: documentsResult.status === 'fulfilled' ? 
          documentsResult.value.data?.data?.por_status?.pendente || 0 : 0,
        documentos_aprovados: documentsResult.status === 'fulfilled' ? 
          documentsResult.value.data?.data?.por_status?.aprovado || 0 : 0,

        // Marketplace
        propostas_marketplace: 0, // TODO: Implementar quando getMarketplaceStats estiver disponível
        propostas_ativas: 0, // TODO: Implementar quando getMarketplaceStats estiver disponível

        // Recebimentos
        recebimentos_mes: receivingsResult.status === 'fulfilled' ? 
          receivingsResult.value.data?.data?.total_mes_atual || 0 : 0,

        // Leads
        total_leads: leadsResult.status === 'fulfilled' ? 
          leadsResult.value.data?.data?.total || 0 : 0,
        leads_novos: leadsResult.status === 'fulfilled' ? 
          leadsResult.value.data?.data?.novos || 0 : 0,
        leads_em_contato: leadsResult.status === 'fulfilled' ? 
          leadsResult.value.data?.data?.em_contato || 0 : 0,
        leads_convertidos: leadsResult.status === 'fulfilled' ? 
          leadsResult.value.data?.data?.convertidos || 0 : 0,
        leads_perdidos: leadsResult.status === 'fulfilled' ? 
          leadsResult.value.data?.data?.perdidos || 0 : 0,
        taxa_conversao_leads: leadsResult.status === 'fulfilled' ? 
          leadsResult.value.data?.data?.taxa_conversao || 0 : 0,
        recebimentos_pendentes: receivingsResult.status === 'fulfilled' ? 
          receivingsResult.value.data?.data?.por_status?.pendente || 0 : 0,

        // Sistema
        funcoes_ativas: 46, // Valor fixo por enquanto
        uptime_sistema: 99.8, // Calculado pelo Firebase
        storage_usado: 0, // Seria calculado via Cloud Storage API

        // Performance
        valor_total_marketplace: 0, // TODO: Implementar quando getMarketplaceStats estiver disponível
        valor_total_recebimentos: receivingsResult.status === 'fulfilled' ? 
          receivingsResult.value.data?.data?.valor_total_mes || 0 : 0
      };

      setStats(systemStats);
      setLastUpdate(new Date());

      // Gerar atividades recentes baseadas nos dados reais
      const activities = generateRecentActivities(systemStats);
      setRecentActivities(activities);

      console.log('✅ SuperadminDashboard: Dados carregados com sucesso', systemStats);

    } catch (error) {
      console.error('❌ SuperadminDashboard: Erro ao carregar dados:', error);
      setError('Erro ao carregar dados do dashboard. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const generateRecentActivities = (systemStats) => {
    const activities = [];
    const now = new Date();

    // Atividades baseadas nos dados reais
    if (systemStats.fornecedores_aprovados > 0) {
      activities.push({
        id: 1,
        tipo: 'fornecedor_aprovado',
        descricao: `${systemStats.fornecedores_aprovados} fornecedores aprovados no sistema`,
        usuario: 'Sistema',
        data: new Date(now - 1000 * 60 * 30),
        status: 'success'
      });
    }

    if (systemStats.documentos_pendentes > 0) {
      activities.push({
        id: 2,
        tipo: 'documentos_pendentes',
        descricao: `${systemStats.documentos_pendentes} documentos aguardando aprovação`,
        usuario: 'Sistema',
        data: new Date(now - 1000 * 60 * 60),
        status: 'warning'
      });
    }

    if (systemStats.propostas_ativas > 0) {
      activities.push({
        id: 3,
        tipo: 'marketplace_ativo',
        descricao: `${systemStats.propostas_ativas} propostas ativas no marketplace`,
        usuario: 'Sistema',
        data: new Date(now - 1000 * 60 * 60 * 2),
        status: 'info'
      });
    }

    if (systemStats.recebimentos_pendentes > 0) {
      activities.push({
        id: 4,
        tipo: 'recebimentos_pendentes',
        descricao: `${systemStats.recebimentos_pendentes} recebimentos pendentes`,
        usuario: 'Sistema',
        data: new Date(now - 1000 * 60 * 60 * 3),
        status: 'warning'
      });
    }

    activities.push({
      id: 5,
      tipo: 'sistema_funcionando',
      descricao: `Sistema operando com ${systemStats.funcoes_ativas} funções ativas`,
      usuario: 'Sistema',
      data: new Date(now - 1000 * 60 * 60 * 4),
      status: 'success'
    });

    return activities.slice(0, 7); // Máximo 7 atividades
  };

  const handleRefresh = () => {
    loadDashboardData();
  };



  // Verificar permissão
  if (!hasPermission('view_dashboard')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Acesso Negado</h3>
          <p className="text-gray-600">Você não tem permissão para visualizar este dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando dados do sistema...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erro ao Carregar</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 mx-auto"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Tentar Novamente</span>
          </button>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total de Empresas',
      value: stats.total_empresas,
      subtitle: `${stats.empresas_ativas} ativas`,
      icon: Building2,
      color: 'bg-blue-500',
      trend: stats.empresas_ativas === stats.total_empresas ? 'Todas ativas' : `${stats.total_empresas - stats.empresas_ativas} inativas`
    },
    {
      title: 'Leads Comerciais',
      value: stats.total_leads,
      subtitle: `${stats.leads_novos} novos, ${stats.leads_em_contato} em contato`,
      icon: Target,
      color: 'bg-cyan-500',
      trend: `${stats.taxa_conversao_leads}% conversão`
    },
    {
      title: 'Leads Convertidos',
      value: stats.leads_convertidos,
      subtitle: `${stats.leads_perdidos} não convertidos`,
      icon: CheckCircle,
      color: 'bg-emerald-500',
      trend: `${stats.leads_novos + stats.leads_em_contato} em andamento`
    },
    {
      title: 'Usuários no Sistema',
      value: stats.total_usuarios,
      subtitle: `${stats.usuarios_ativos} ativos`,
      icon: Users,
      color: 'bg-green-500',
      trend: `${Math.round((stats.usuarios_ativos / stats.total_usuarios) * 100)}% ativos`
    },
    {
      title: 'Fornecedores',
      value: stats.total_fornecedores,
      subtitle: `${stats.fornecedores_aprovados} aprovados`,
      icon: Truck,
      color: 'bg-purple-500',
      trend: `${Math.round((stats.fornecedores_aprovados / stats.total_fornecedores) * 100)}% aprovados`
    },
    {
      title: 'Ingredientes Cadastrados',
      value: stats.total_produtos,
      subtitle: `${stats.produtos_ativos} ativos`,
      icon: Package,
      color: 'bg-orange-500',
      trend: `${Math.round((stats.produtos_ativos / stats.total_produtos) * 100)}% ativos`
    },
    {
      title: 'Documentos Pendentes',
      value: stats.documentos_pendentes,
      subtitle: `${stats.documentos_aprovados} aprovados`,
      icon: AlertTriangle,
      color: 'bg-yellow-500',
      trend: stats.documentos_pendentes > 0 ? 'Requer atenção' : 'Tudo em dia'
    },
    {
      title: 'Recebimentos (Mês)',
      value: stats.recebimentos_mes,
      subtitle: `${stats.recebimentos_pendentes} pendentes`,
      icon: CheckCircle,
      color: 'bg-teal-500',
      trend: `R$ ${(stats.valor_total_recebimentos / 1000).toFixed(0)}k`
    },
    {
      title: 'Marketplace',
      value: stats.propostas_marketplace,
      subtitle: `${stats.propostas_ativas} ativas`,
      icon: TrendingUp,
      color: 'bg-indigo-500',
      trend: `R$ ${(stats.valor_total_marketplace / 1000).toFixed(0)}k em propostas`
    },
    {
      title: 'Funções Ativas',
      value: stats.funcoes_ativas,
      subtitle: `${stats.uptime_sistema}% uptime`,
      icon: Activity,
      color: 'bg-red-500',
      trend: 'Sistema estável'
    }
  ];

  const getActivityIcon = (tipo) => {
    switch (tipo) {
      case 'fornecedor_aprovado':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'documentos_pendentes':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'marketplace_ativo':
        return <TrendingUp className="h-4 w-4 text-blue-500" />;
      case 'recebimentos_pendentes':
        return <Clock className="h-4 w-4 text-orange-500" />;
      case 'sistema_funcionando':
        return <Activity className="h-4 w-4 text-green-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const getActivityColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Superadmin</h1>
          <p className="text-gray-600">Visão geral completa do sistema QualiManager</p>
          {lastUpdate && (
            <p className="text-sm text-gray-500 mt-1">
              Última atualização: {lastUpdate.toLocaleTimeString()}
            </p>
          )}
        </div>
        <button
          onClick={handleRefresh}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Atualizar</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className={`${card.color} p-3 rounded-lg`}>
                <card.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                <p className="text-sm text-gray-600">{card.title}</p>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600">{card.subtitle}</p>
              <p className="text-xs text-gray-500 mt-1">{card.trend}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts and Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Overview */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Visão Geral do Sistema</h3>
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Taxa de Aprovação de Fornecedores</span>
              <span className="text-sm font-medium text-gray-900">
                {Math.round((stats.fornecedores_aprovados / stats.total_fornecedores) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full" 
                style={{ width: `${(stats.fornecedores_aprovados / stats.total_fornecedores) * 100}%` }}
              ></div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Usuários Ativos</span>
              <span className="text-sm font-medium text-gray-900">
                {Math.round((stats.usuarios_ativos / stats.total_usuarios) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full" 
                style={{ width: `${(stats.usuarios_ativos / stats.total_usuarios) * 100}%` }}
              ></div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Ingredientes Ativos</span>
              <span className="text-sm font-medium text-gray-900">
                {Math.round((stats.produtos_ativos / stats.total_produtos) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-500 h-2 rounded-full" 
                style={{ width: `${(stats.produtos_ativos / stats.total_produtos) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Atividades do Sistema</h3>
            <Activity className="h-5 w-5 text-gray-400" />
          </div>
          
          <div className="space-y-3">
            {recentActivities.map((activity) => (
              <div 
                key={activity.id} 
                className={`p-3 rounded-lg border ${getActivityColor(activity.status)}`}
              >
                <div className="flex items-start space-x-3">
                  {getActivityIcon(activity.tipo)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{activity.descricao}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {activity.data.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>



      {/* System Health */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Saúde do Sistema</h3>
          <Database className="h-5 w-5 text-gray-400" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-green-100 p-4 rounded-lg mb-2">
              <CheckCircle className="h-8 w-8 text-green-600 mx-auto" />
            </div>
            <p className="text-sm font-medium text-gray-900">Sistema Online</p>
            <p className="text-xs text-gray-500">{stats.uptime_sistema}% uptime</p>
          </div>
          
          <div className="text-center">
            <div className="bg-blue-100 p-4 rounded-lg mb-2">
              <Globe className="h-8 w-8 text-blue-600 mx-auto" />
            </div>
            <p className="text-sm font-medium text-gray-900">Funções Ativas</p>
            <p className="text-xs text-gray-500">{stats.funcoes_ativas} functions</p>
          </div>
          
          <div className="text-center">
            <div className="bg-purple-100 p-4 rounded-lg mb-2">
              <Shield className="h-8 w-8 text-purple-600 mx-auto" />
            </div>
            <p className="text-sm font-medium text-gray-900">Segurança</p>
            <p className="text-xs text-gray-500">Todos os módulos protegidos</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperadminDashboard;

